import { useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import useInput from "../../hooks/userInput";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import {
  BsPersonFill,
  BsKeyFill,
  BsEyeFill,
  BsEyeSlashFill,
} from "react-icons/bs";
import {
  HiArrowRightOnRectangle,
  HiOutlineKey,
  HiOutlineLockClosed,
} from "react-icons/hi2";
import { Helmet } from "react-helmet-async";

import cf from "../UI/ClassicForm.module.css";
import { logIn, verifyLoginTwoFactor } from "../../api/api";
import { authActions } from "../../store/auth-slice";
import { alertActions } from "../../store/alert-slice";
import Spinner from "../UI/Spinner";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [passwordType, setPasswordType] = useState("password");
  const [showSpinner, setShowSpinner] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const dispatch = useDispatch();
  const setCookie = useCookies(["jwt"])[1];
  const navigate = useNavigate();

  const {
    value: usernameInput,
    enteredValueIsValid: usernameInputIsValid,
    hasError: usernameInputIsInvalid,
    valueInputChangedHandler: usernameInputChangedHandler,
    valueInputBlurHandler: usernameInputBlurHandler,
  } = useInput((value) => value.trim() !== "");

  const {
    value: passwordInput,
    enteredValueIsValid: passwordInputIsValid,
    hasError: passwordInputIsInvalid,
    valueInputChangedHandler: passwordInputChangedHandler,
    valueInputBlurHandler: passwordInputBlurHandler,
    reset: passwordInputReset,
  } = useInput((value) => value.trim() !== "");

  const switchEyeIcon = () => {
    setShowPassword((initialstate) => !initialstate);
  };

  const switchType = (val) => {
    setPasswordType(val);
  };

  const passwordActionSee = () => {
    switchEyeIcon();
    switchType("text");
  };
  const passwordActioBlind = () => {
    switchEyeIcon();
    switchType("password");
  };

  const isVerifyingCode = !!twoFactorToken;
  const codeIsValid = /^\d{6}$/.test(verificationCode.trim());
  let formIsValid = false;

  if (isVerifyingCode) {
    formIsValid = codeIsValid;
  } else if (usernameInputIsValid && passwordInputIsValid) {
    formIsValid = true;
  }

  const submitHandler = async (e) => {
    e.preventDefault();
    setShowSpinner(true);

    const res = isVerifyingCode
      ? await verifyLoginTwoFactor({
          code: verificationCode.trim(),
          twoFactorToken,
        })
      : await logIn({
          username: usernameInput,
          password: passwordInput,
        });

    if (res.status === "success") {
      if (res.requiresTwoFactor) {
        setTwoFactorToken(res.twoFactorToken);
        dispatch(
          alertActions.setState({ message: res.message, status: res.status })
        );
      } else {
        dispatch(authActions.login({ user: res.data.user }));
        dispatch(
          alertActions.setState({ message: res.message, status: res.status })
        );
        setCookie("jwt", res.token);
        navigate("/account", { replace: true });
      }
    } else {
      dispatch(
        alertActions.setState({ message: res.message, status: "error" })
      );
    }

    setShowSpinner(false);
    if (!isVerifyingCode) {
      passwordInputReset();
    } else {
      setVerificationCode("");
    }
    setTimeout(() => {
      dispatch(alertActions.resetState());
    }, 3000);
  };

  const usernameInputClasses = usernameInputIsInvalid
    ? `${cf.field} ${cf.invalid}`
    : cf.field;

  const passwordInputClasses = passwordInputIsInvalid
    ? `${cf.field} ${cf.invalid}`
    : cf.field;
  return (
    <Fragment>
      <Helmet>
        <title>Login</title>
        <meta name="description" content="" />
        <link rel="canonical" href="/Login" />
      </Helmet>
      <form className={cf.form} onSubmit={submitHandler}>
        {showSpinner && <Spinner />}
        <div className={cf.formTitleRow}>
          <HiOutlineLockClosed className={cf.formTitleIcon} aria-hidden />
          <h2 className={cf.formTitle}>Sign in</h2>
        </div>
        <p className={cf.formSubtitle}>
          {isVerifyingCode
            ? "Enter the 6-digit code sent to your email to continue."
            : "Access your secure Online Banking with your username and password."}
        </p>
        {!isVerifyingCode && (
          <div className={usernameInputClasses}>
            <label htmlFor="username">Username</label>
            <div className={cf.inputGroup}>
              <BsPersonFill className={cf.icon} />
              <input
                id="username"
                type="text"
                autoComplete="username"
                value={usernameInput}
                onChange={usernameInputChangedHandler}
                onBlur={usernameInputBlurHandler}
              />
            </div>
          </div>
        )}

        {!isVerifyingCode && (
          <div className={passwordInputClasses}>
            <label htmlFor="password">Password</label>
            <div className={cf.inputGroup}>
              <BsKeyFill className={cf.icon} />
              <input
                id="password"
                type={passwordType}
                autoComplete="current-password"
                value={passwordInput}
                onChange={passwordInputChangedHandler}
                onBlur={passwordInputBlurHandler}
              />
              {!showPassword && (
                <BsEyeFill
                  className={`${cf.icon} ${cf.iconButton}`}
                  onClick={passwordActionSee}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && passwordActionSee()
                  }
                  aria-label="Show password"
                />
              )}
              {showPassword && (
                <BsEyeSlashFill
                  className={`${cf.icon} ${cf.iconButton}`}
                  onClick={passwordActioBlind}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && passwordActioBlind()
                  }
                  aria-label="Hide password"
                />
              )}
            </div>
            <Link to="/forgotPassword" className={cf.linkForgot}>
              <HiOutlineKey className={cf.linkForgotIcon} aria-hidden />
              Forgot password?
            </Link>
          </div>
        )}

        {isVerifyingCode && (
          <div className={cf.field}>
            <label htmlFor="verificationCode">Verification code</label>
            <div className={cf.inputGroup}>
              <BsKeyFill className={cf.icon} />
              <input
                id="verificationCode"
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(e.target.value.replace(/\D/g, ""))
                }
              />
            </div>
          </div>
        )}
        <div className={cf.actions}>
          <button type="submit" disabled={!formIsValid}>
            <HiArrowRightOnRectangle className={cf.btnIcon} aria-hidden />
            {isVerifyingCode ? "Verify code" : "Login"}
          </button>
        </div>
      </form>
    </Fragment>
  );
};

export default Login;
